import {
    Breakpoints,
    Checkbox,
    CTA,
    DisplayTag,
    Fieldset,
    Infobox,
    InfoboxAppearanceValue,
    InputAppearanceValue,
    RadioButton,
    Select,
    Text,
    TextInput,
    TextTag,
    TokenTextAppearance,
} from '@volkswagen-onehub/components-core'

import {Controller, FieldErrors, useForm} from 'react-hook-form'
import axios from '../../../axiosConfig'
import styled from 'styled-components'
import React, {useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useDispatch, useSelector} from 'react-redux'
import {openDisclaimerLayer} from '../layers/launchers/DisclaimerLayerHandle'
import isEmpty from 'lodash.isempty'
import {ErrorSummary} from './ErrorSummary'
import {UserDataForm} from '../../../types/userData'
import {Loading} from '../../../common/components/general/loading'
import {openAlertSystemLayer} from '../layers/launchers/AlertSystemLayerHandle'
import {AppDispatch, fetchAllDealers, fetchAllProvinces} from '../../thunk'
import {
    setPersonalData,
    setSelectedFormModel,
    showThanksPage,
} from '../../../features/userData/userDataSlice'
import {RootState} from '../../rootReducer'
import {LayerForm} from '../../../shared/classes'
import {
    authProxyUserEndpoint,
    axiosInstance,
    csrfToken,
} from '../../../shared/authServiceHelper'
import {Dealer} from '../../../types/dealer'
import {
    capitalizeWords,
    getDealerAddress,
    getDealerInfoOption,
} from '../../../shared/utilities'
import {Global, Params, RequestStatus} from '../../../shared/variables'
import {setLoading} from '../../../features/general/generalSlice'
import {setDealerCode} from '../../../features/dealerData/dealerDataSlice'
import SingleModelInterface from '../../../types/singleModelInterface'
import TrackingAction from '../../../tracking-manager'
import {
    getFormBasicClickData,
    getFormErrorMessageLoadData,
    getFormSelectedModelData,
    getFormSubmissionSuccessMessageLoadData,
} from '../../../utils/tracking/getTrackingData'
import setLocalTracking from '../../../utils/tracking/tracking-utils/setLocalTracking'
import {titleCaseItalian} from '../../../utils/provinces/titleCaseItalian'
import {getApiToken} from '../../../shared/apiToken'
import {getTrackingModel} from '../../../utils/tracking/tracking-utils/getTrackingModel'
import {getV2BaseFromBackendUrl} from '../../../utils/url/getV2BaseFromBackendUrl'
import {
    DealerSelectionStatus,
    getDealerSelectionStatus,
} from '../../../utils/dealers/getDealerSelectionStatus'
import {DealerSelectionFeedback} from './DealerSelectionFeedback'

const FieldContainer = styled.div`
    margin: 0 0 ${(props) => props.theme.size.dynamic0100};
`

const CouponLayerForm = styled(LayerForm)`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: stretch;
`

// Covers the form while it is being submitted, without moving the content
// around it (e.g. the legal notes of the promotion)
const FormLoadingOverlay = styled(Loading)`
    && {
        position: absolute;
        inset: 0;
        height: auto;
        z-index: 2;
    }
`

const FormPrivacyContainer = styled.div`
    position: relative;
    border: 2px solid ${(props) => props.theme.colors.border.legalframe};
    border-radius: ${(props) => props.theme.size.borderRadius.small};
    padding: ${(props) => props.theme.size.dynamic0270}
        ${(props) => props.theme.size.dynamic0100}
        ${(props) => props.theme.size.dynamic0200}
        ${(props) => props.theme.size.dynamic0100};

    margin: ${(props) => props.theme.size.static600} 0
        ${(props) => props.theme.size.dynamic0150};

    @media (min-width: ${Breakpoints.b560}px) {
        padding: ${(props) => props.theme.size.dynamic0150}
            ${(props) => props.theme.size.dynamic0100};
        margin: ${(props) => props.theme.size.dynamic0300} 0 0;
    }
`
const FormPrivacyContainerTitle = styled(Text)`
    position: absolute;
    top: -13px;
    left: 50%;
    transform: translateX(-50%);
    background: ${(props) => props.theme.colors.background.primary};
    padding: 0 ${(props) => props.theme.size.static200};
    color: ${(props) => props.theme.colors.content.active};
    text-align: center;
    white-space: nowrap;
`
const DealerCard = styled.div`
    margin: 0 0 ${(props) => props.theme.size.dynamic0020};
    padding: ${(props) => props.theme.size.dynamic0050};
    border: ${(props) => props.theme.size.border.active} solid
        ${(props) => props.theme.colors.border.divider};
    border-radius: ${(props) => props.theme.size.borderRadius.card};

    legend {
        font-weight: normal;
    }
`
const DealerCardTitle = styled(Text)`
    margin-bottom: ${(props) => props.theme.size.static150};
`
const PrivacyText = styled(Text)`
    margin: ${(props) => props.theme.size.static150} 0
        ${(props) => props.theme.size.static150};
`
const PreCompiledCTA = styled(CTA)`
    margin: ${(props) => props.theme.size.dynamic0100} 0
        ${(props) => props.theme.size.dynamic0200};
`
const CTAVum = styled(CTA)`
    display: block;
    margin: 0 0 ${(props) => props.theme.size.static400} auto;
    color: ${({theme}) => theme.colors.content.secondary};
`
const StyledSelectMessage = styled.div`
    position: absolute;
    left: -9999px;
`

const DealerDisplayTag = styled(DisplayTag)`
    border-radius: ${({theme}) => theme.size.borderRadius.displayTag};
    height: fit-content;
    margin: 0 0 ${(props) => props.theme.size.dynamic0100};
    padding: ${(props) => props.theme.size.dynamic0050};
    white-space: wrap;
    width: 100%;
`

export const COUPON_REQUEST_FORM_ID = 'coupon-request-form'

// tslint:disable-next-line:cyclomatic-complexity
export function CouponRequestForm(): React.JSX.Element {
    const {t} = useTranslation('app')
    const dispatch = useDispatch() as AppDispatch

    const [loadingForm, setLoadingForm] = useState(false)
    const [showNotification, setShowNotification] = useState(false)
    const [NotificationText, setNotificationText] = useState('')

    const backendUrl = useSelector(
        (state: RootState) => state.general.backendUrl
    )
    const model = useSelector((state: RootState) => state.general.singleCar)
    const selectedPromotion = useSelector(
        (state: RootState) => state.general.selectedPromotion
    )
    const logged = useSelector((state: RootState) => state.general.logged)
    const allDealers: Dealer[] = useSelector(
        (state: RootState) => state.dealerData.allDealers
    )
    const myDealerCode = useSelector(
        (state: RootState) => state.dealerData.dealerCode
    )
    const dealerBranches = useSelector(
        (state: RootState) => state.dealerData.dealerBranches
    )
    const campaignId = useSelector(
        (state: RootState) => state.general.campaignId
    )
    const models = useSelector(
        (state: RootState) => state.general.modelsWithActivePromotions
    )
    const allProvinces = useSelector(
        (state: RootState) => state.provinces.allProvinces
    )
    const provincesStatus = useSelector(
        (state: RootState) => state.provinces.status
    )
    const dealersStatus = useSelector(
        (state: RootState) => state.dealerData.allDealersStatus
    )

    const trackingBrand = useSelector(
        (state: RootState) => state.general.trackingBrand
    )
    const brandFromState = useSelector(
        (state: RootState) => state.general.brand
    )
    const isShowroom = useSelector(
        (state: RootState) => state.general.isShowroom
    )
    const selectedFormModel = useSelector(
        (state: RootState) => state.userData.selectedFormModel
    )
    const cycleID = useSelector(
        (state: RootState) => state.userData.formCycleID
    )

    const [filteredDealers, setFilteredDealers] = useState<Dealer[]>([])
    const [provinceStatusMessage, setProvinceStatusMessage] = useState<string>(
        t('form.province.noneSelected')
    )
    const [dealerStatusMessage, setDealerStatusMessage] = useState<string>(
        t('form.dealer.noneSelected')
    )
    const [modelStatusMessage, setModelStatusMessage] = useState<string>(
        t('form.model.noneSelected')
    )
    const formRef = useRef<HTMLFormElement>(null)

    // Loads the lists of the dealer selection, unless already there or loading
    const fetchDealerSelectionData = () => {
        if (
            isEmpty(allProvinces) &&
            provincesStatus !== RequestStatus.LOADING
        ) {
            dispatch(fetchAllProvinces())
        }
        if (isEmpty(allDealers) && dealersStatus !== RequestStatus.LOADING) {
            dispatch(fetchAllDealers())
        }
    }

    const dealerSelectionStatus = getDealerSelectionStatus({
        provincesStatus,
        dealersStatus,
        hasProvinces: !isEmpty(allProvinces),
        hasDealers: !isEmpty(allDealers),
    })
    const isDealerSelectionReady =
        dealerSelectionStatus === DealerSelectionStatus.READY

    useEffect(() => {
        fetchDealerSelectionData()
    }, [])

    useEffect(() => {
        if (dealerBranches && dealerBranches.length === 1) {
            const code = dealerBranches[0].companyCode
            saveDealerSelected(code, false)
            setValue('dealer', code)
            clearErrors('dealer')
        }
    }, [dealerBranches])

    const filterDealersByProvince = (province: string) => {
        setFilteredDealers(
            allDealers.filter(
                (dealer: Dealer) =>
                    dealer.province?.toLowerCase() === province.toLowerCase() &&
                    dealer.brand === brandFromState
            )
        )
        setProvinceStatusMessage(
            province
                ? t('form.province.selected', {
                      province,
                  })
                : t('form.province.noneSelected')
        )
    }

    const saveDealerSelected = (
        dealerCodeSelected: string,
        fromAllDealers = true
    ) => {
        const dealersToSearch = fromAllDealers ? allDealers : dealerBranches
        const selected = dealersToSearch.find(
            (dealer: Dealer) => dealer.companyCode === dealerCodeSelected
        )
        setDealerStatusMessage(
            selected
                ? t('form.dealer.selected', {
                      dealer: capitalizeWords(getDealerInfoOption(selected)),
                  })
                : t('form.dealer.noneSelected')
        )
    }

    const emailRegExp = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i

    const {
        register,
        handleSubmit,
        setError,
        reset,
        setValue,
        clearErrors,
        control,
        formState,
        getValues,
        setFocus,
        formState: {errors},
    } = useForm<UserDataForm>({
        mode: 'onChange',
        shouldFocusError: true,
    })

    useEffect(() => {
        const form = formRef.current
        if (!form) {
            return
        }

        const requiredFields = [
            'models',
            'name',
            'surname',
            'email',
            'phone',
            'province',
            'dealer',
            'privacy_consent',
        ]

        requiredFields.forEach((name) => {
            form.querySelectorAll<HTMLElement>(`[name="${name}"]`).forEach(
                (field) => {
                    field.setAttribute('aria-required', 'true')
                }
            )
        })

        const selectFields = [
            {
                name: 'models',
                statusId: 'models-status-message',
                errorId: 'models-error-message',
                hasError: !!errors.models,
            },
            {
                name: 'province',
                statusId: 'province-status-message',
                errorId: 'province-error-message',
                hasError: !!errors.province,
            },
            {
                name: 'dealer',
                statusId: 'dealer-status-message',
                errorId: 'dealer-error-message',
                hasError: !!errors.dealer,
            },
        ]

        selectFields.forEach(({name, statusId, errorId, hasError}) => {
            const select = form.querySelector<HTMLSelectElement>(
                `select[name="${name}"]`
            )

            if (!select) {
                return
            }

            select.setAttribute(
                'aria-describedby',
                hasError ? `${errorId} ${statusId}` : statusId
            )
        })
    })

    // tslint:disable-next-line:no-shadowed-variable
    const showBackendValidationForm = (field: string, errors: any) => {
        if (field in errors) {
            // @ts-ignore
            setError(field, {
                type: 'manual',
                message: errors[String(field)].map((el: string) => el),
            })
        }
        focusFirstError(errors)
    }

    const resetErrorNotification = () => {
        setShowNotification(false)
        setNotificationText('')
    }

    const getFetchedDealerFromCode = (): Dealer | null =>
        Array.isArray(dealerBranches)
            ? dealerBranches.length === 1
                ? dealerBranches[0]
                : dealerBranches.find(
                      (dealerData: Dealer) =>
                          dealerData.companyCode === myDealerCode
                  ) || null
            : null

    const getLeadCodeFromDealerField = (
        dealerFieldValue: string
    ): string | undefined => {
        const dealer =
            allDealers.find(
                (d: Dealer) =>
                    d.companyCode === dealerFieldValue ||
                    d.leadCode === dealerFieldValue
            ) ||
            dealerBranches.find(
                (d: Dealer) =>
                    d.companyCode === dealerFieldValue ||
                    d.leadCode === dealerFieldValue
            )

        return dealer?.leadCode
    }

    const postRFQ = async (userData: UserDataForm) => {
        setLoadingForm(true)

        const backendUrlApiV2 = getV2BaseFromBackendUrl(backendUrl)

        const trackingModel = getTrackingModel(model, selectedFormModel)
        const selectedModelId = model?.id || getValues('models')

        const submitQuotation = async (apiToken: string | null) =>
            axios.post(
                `${backendUrlApiV2}/${Params.QUOTATIONS}`,
                {
                    name: userData.name,
                    surname: userData.surname,
                    email: userData.email,
                    phone: userData.phone,
                    company_code: userData.dealer,
                    dealer_code:
                        getLeadCodeFromDealerField(userData.dealer) ||
                        userData.dealer,
                    privacy_consent: userData.privacy_consent,
                    profiling_consent: userData.profiling_consent,
                    marketing_consent: userData.marketing_consent,
                    ...(selectedModelId && {car_id: selectedModelId}),
                    ...(selectedPromotion?.id && {
                        promotion_id: selectedPromotion.id,
                    }),
                    ...(campaignId && {lead_inbox_campaign_id: campaignId}),
                },
                {
                    withCredentials: false,
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'X-API-TOKEN': apiToken || '',
                    },
                }
            )

        const handleSuccess = (response: any) => {
            setLoadingForm(false)
            dispatch(showThanksPage(true))
            dispatch(setPersonalData(userData))

            setLocalTracking(
                TrackingAction.VWBASIC_FORM_SUBMISSION_SUCCESS_MESSAGE_LOAD,
                getFormSubmissionSuccessMessageLoadData(
                    trackingModel?.name,
                    trackingModel?.slug,
                    trackingBrand,
                    isShowroom,
                    userData,
                    response.data.id,
                    model && model.id,
                    selectedPromotion?.title,
                    selectedPromotion?.motorization,
                    cycleID
                )
            )

            resetErrorNotification()
            reset()
        }

        const handleError = (error: any) => {
            setLoadingForm(false)

            if (error?.response?.status === Global.ERROR_422) {
                Object.keys(error.response.data.errors).forEach((el) => {
                    showBackendValidationForm(el, error.response.data.errors)
                })
                trackingError()
            } else {
                openAlertSystemLayer()
                console.log(error)
            }
        }

        try {
            const apiToken = await getApiToken(backendUrlApiV2)
            try {
                const response = await submitQuotation(apiToken)
                handleSuccess(response)
            } catch (error: any) {
                if (error?.response?.status === 401) {
                    try {
                        const refreshedToken = await getApiToken(
                            backendUrlApiV2,
                            true
                        )
                        const response = await submitQuotation(refreshedToken)
                        handleSuccess(response)
                    } catch (retryError: any) {
                        handleError(retryError)
                    }
                } else {
                    handleError(error)
                }
            }
        } catch (tokenError: any) {
            handleError(tokenError)
        }
    }

    const trackingBasicClick = () => {
        const trackingModel = getTrackingModel(model, selectedFormModel)

        setLocalTracking(
            TrackingAction.VWBASIC_FORM_CTA_CLICK,
            getFormBasicClickData(
                trackingModel?.name,
                trackingModel?.slug,
                trackingBrand,
                isShowroom,
                model && model.id,
                selectedPromotion?.title,
                selectedPromotion?.motorization
            )
        )
    }

    const trackingError = () => {
        const trackingModel = getTrackingModel(model, selectedFormModel)

        trackingBasicClick()
        setLocalTracking(
            TrackingAction.VWBASIC_FORM_ERROR_MESSAGE_LOAD,
            getFormErrorMessageLoadData(
                trackingModel?.name,
                trackingModel?.slug,
                trackingBrand,
                isShowroom,
                model && model.id,
                Object.keys(errors),
                selectedPromotion?.title,
                selectedPromotion?.motorization,
                cycleID
            )
        )
    }

    const trackingSelectedModel = (selectedModel: SingleModelInterface) => {
        setLocalTracking(
            TrackingAction.VWBASIC_FORM_MODEL_SELECT_CLICK,
            getFormSelectedModelData(
                selectedModel,
                trackingBrand,
                isShowroom,
                selectedPromotion?.title,
                selectedPromotion?.motorization
            )
        )
    }

    const onSubmit = (data: UserDataForm) => {
        resetErrorNotification()
        postRFQ(data)
    }

    const FIELD_ORDER: (keyof UserDataForm)[] = [
        'models',
        'name',
        'surname',
        'email',
        'phone',
        'province',
        'dealer',
        'privacy_consent',
    ]

    function focusFirstError(errs: FieldErrors<UserDataForm>): void {
        // eslint-disable-next-line security/detect-object-injection
        const first = FIELD_ORDER.find((f) => !!errs[f])
        if (!first) {
            {
                return
            }
        }
        setFocus(first)
        window.requestAnimationFrame(() => {
            formRef.current
                ?.querySelector<HTMLElement>(`[name="${String(first)}"]`)
                ?.focus()
        })
    }

    const onError = (errs: typeof errors) => {
        trackingError()
        focusFirstError(errs)
    }

    const preCompileForm = () => {
        if (csrfToken) {
            setLoadingForm(true)

            axiosInstance
                .get(authProxyUserEndpoint)
                .then(
                    (res: {
                        data: {
                            given_name: string
                            family_name: string
                            phone_number: string
                            email: string
                        }
                    }) => {
                        if (res.data.given_name) {
                            setValue('name', res.data.given_name)
                            clearErrors('name')
                        }

                        if (res.data.family_name) {
                            setValue('surname', res.data.family_name)
                            clearErrors('surname')
                        }

                        if (res.data.phone_number) {
                            setValue('phone', res.data.phone_number)
                            clearErrors('phone')
                        }

                        if (res.data.email) {
                            setValue('email', res.data.email)
                            clearErrors('email')
                        }

                        setLoadingForm(false)
                    }
                )
                // tslint:disable-next-line:no-shadowed-variable
                .catch((errors: string | undefined) => {
                    dispatch(setLoading(false))
                    throw new Error(errors)
                })
        } else {
            openAlertSystemLayer()
            throw new Error('csrfToken missed')
        }
    }

    const changeDealer = () => {
        dispatch(setDealerCode(''))
    }

    const dealerBranchCodes =
        Array.isArray(dealerBranches) && dealerBranches.length > 0
            ? dealerBranches.map((branch: Dealer) => branch.bearer)
            : []

    const fetchedDealer = getFetchedDealerFromCode()

    const isDealerizedUser =
        Boolean(myDealerCode) || dealerBranchCodes.length > 0

    const myDealerCodesToCheck = [
        ...(myDealerCode ? [myDealerCode] : []),
        ...dealerBranchCodes,
    ]

    const myDealerIsInPromo =
        !isEmpty(allDealers) &&
        myDealerCodesToCheck.length > 0 &&
        allDealers.some((dealer: Dealer) =>
            myDealerCodesToCheck.includes(dealer.companyCode)
        )

    const showDealerNotInPromoBanner =
        !isEmpty(allDealers) && isDealerizedUser && !myDealerIsInPromo

    return (
        <>
            {logged ? (
                <PreCompiledCTA
                    tag="button"
                    emphasis="secondary"
                    size="small"
                    onClick={preCompileForm}
                    aria-label={t('coupon.couponRequest.precompiledCtaLabel')}
                    title={t('coupon.couponRequest.precompiledCtaLabel')}
                >
                    <span
                        dangerouslySetInnerHTML={{
                            __html: t(
                                'coupon.couponRequest.precompiledCtaLabel'
                            ),
                        }}
                    />
                </PreCompiledCTA>
            ) : undefined}

            <CouponLayerForm
                id={COUPON_REQUEST_FORM_ID}
                ref={formRef}
                onSubmit={handleSubmit(onSubmit, onError)}
                className={!logged ? 'no-logged' : undefined}
                aria-busy={loadingForm || undefined}
            >
                <ErrorSummary
                    errors={errors}
                    isSubmitted={formState.isSubmitted}
                />
                <div>
                    {models && !model.id ? (
                        <>
                            <FieldContainer>
                                <Controller
                                    control={control}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: t('form.error.required'),
                                        },
                                    }}
                                    name="models"
                                    render={({field: {onChange}}) => (
                                        // @ts-ignore
                                        <Select
                                            defaultValue=""
                                            name="models"
                                            onChange={(val) => {
                                                onChange(val)
                                                const selectedCardId = Number(
                                                    val.currentTarget.value
                                                )

                                                const selectedModel = models
                                                    .slice()
                                                    .filter(
                                                        (
                                                            singleModel: SingleModelInterface
                                                        ) =>
                                                            !!singleModel.model_deriv
                                                    )
                                                    .find(
                                                        (
                                                            singleModel: SingleModelInterface
                                                        ) =>
                                                            singleModel.id ===
                                                            selectedCardId
                                                    )

                                                const modelDisplayName =
                                                    selectedModel?.carline_name ||
                                                    ''

                                                dispatch(
                                                    setSelectedFormModel(
                                                        selectedModel
                                                    )
                                                )

                                                setModelStatusMessage(
                                                    modelDisplayName
                                                        ? t(
                                                              'form.model.selected',
                                                              {
                                                                  model: modelDisplayName,
                                                              }
                                                          )
                                                        : t(
                                                              'form.model.noneSelected'
                                                          )
                                                )

                                                if (selectedModel) {
                                                    trackingSelectedModel(
                                                        selectedModel
                                                    )
                                                }
                                            }}
                                            label={t('form.models.label')}
                                            {...(errors.models
                                                ? {
                                                      appearance: 'error',
                                                      message:
                                                          errors.models
                                                              .message || '',
                                                  }
                                                : {})}
                                        >
                                            <option aria-selected value="">
                                                {t('form.models.default')}
                                            </option>

                                            {models
                                                .slice()
                                                .filter(
                                                    (
                                                        singleModel: SingleModelInterface
                                                    ) =>
                                                        !!singleModel.model_deriv
                                                )
                                                .sort(
                                                    (
                                                        singleModelA: SingleModelInterface,
                                                        singleModelB: SingleModelInterface
                                                    ) =>
                                                        singleModelA.carline_name.localeCompare(
                                                            singleModelB.carline_name
                                                        )
                                                )
                                                .map(
                                                    (
                                                        singleModel: SingleModelInterface
                                                    ) => (
                                                        <option
                                                            aria-selected
                                                            key={singleModel.id}
                                                            value={
                                                                singleModel.id
                                                            }
                                                        >
                                                            {
                                                                singleModel.carline_name
                                                            }
                                                        </option>
                                                    )
                                                )}
                                        </Select>
                                    )}
                                />
                            </FieldContainer>
                            <StyledSelectMessage
                                id="models-status-message"
                                role="status"
                                aria-live="polite"
                            >
                                {modelStatusMessage}
                            </StyledSelectMessage>
                            {errors.models ? (
                                <StyledSelectMessage
                                    id="models-error-message"
                                    role="alert"
                                    aria-live="assertive"
                                >
                                    {errors.models.message}
                                </StyledSelectMessage>
                            ) : undefined}
                        </>
                    ) : undefined}

                    <FieldContainer>
                        <TextInput
                            {...register('name', {
                                minLength: {
                                    value: Number(
                                        Global.MINiMUM_FORM_FIELD_LENGTH
                                    ),
                                    message: t('form.error.minLength'),
                                },
                                required: {
                                    value: true,
                                    message: t('form.error.required'),
                                },
                            })}
                            appearance={
                                errors.name
                                    ? ('error' as InputAppearanceValue)
                                    : ('default' as InputAppearanceValue)
                            }
                            message={
                                errors.name ? errors.name.message : undefined
                            }
                            label={t('form.name')}
                            autoComplete="given-name"
                        />
                    </FieldContainer>

                    <FieldContainer>
                        <TextInput
                            {...register('surname', {
                                minLength: {
                                    value: Number(
                                        Global.MINiMUM_FORM_FIELD_LENGTH
                                    ),
                                    message: t('form.error.minLength'),
                                },
                                required: {
                                    value: true,
                                    message: t('form.error.required'),
                                },
                            })}
                            appearance={
                                errors.surname
                                    ? ('error' as InputAppearanceValue)
                                    : ('default' as InputAppearanceValue)
                            }
                            message={
                                errors.surname
                                    ? errors.surname.message
                                    : undefined
                            }
                            label={t('form.surname')}
                            autoComplete="family-name"
                        />
                    </FieldContainer>

                    <FieldContainer>
                        <TextInput
                            {...register('email', {
                                pattern: {
                                    value: emailRegExp,
                                    message: t('form.error.emailNotValid'),
                                },
                                required: {
                                    value: true,
                                    message: t('form.error.required'),
                                },
                            })}
                            appearance={
                                errors.email
                                    ? ('error' as InputAppearanceValue)
                                    : ('default' as InputAppearanceValue)
                            }
                            message={
                                errors.email ? errors.email.message : undefined
                            }
                            label={t('form.email')}
                            autoComplete="email"
                        />
                    </FieldContainer>

                    <FieldContainer>
                        <TextInput
                            {...register('phone', {
                                required: {
                                    value: true,
                                    message: t('form.error.required'),
                                },
                            })}
                            appearance={
                                errors.phone
                                    ? ('error' as InputAppearanceValue)
                                    : ('default' as InputAppearanceValue)
                            }
                            message={
                                errors.phone ? errors.phone.message : undefined
                            }
                            label={t('form.phone')}
                            autoComplete="tel"
                        />
                    </FieldContainer>

                    {showDealerNotInPromoBanner ? (
                        <DealerDisplayTag
                            appearance="alert"
                            text={t('form.myDealer.notActivePromotion.alert')}
                        />
                    ) : undefined}

                    {!myDealerCode || showDealerNotInPromoBanner ? (
                        <>
                            {isDealerSelectionReady ? (
                                <>
                                    <FieldContainer>
                                        <Controller
                                            control={control}
                                            rules={{
                                                required: {
                                                    value: true,
                                                    message: t(
                                                        'form.error.required'
                                                    ),
                                                },
                                            }}
                                            name="province"
                                            render={({field: {onChange}}) => (
                                                // @ts-ignore
                                                <Select
                                                    defaultValue=""
                                                    name="province"
                                                    onChange={(val) => {
                                                        onChange(val)
                                                        filterDealersByProvince(
                                                            val.currentTarget
                                                                .value
                                                        )
                                                    }}
                                                    label={t(
                                                        'form.province.label'
                                                    )}
                                                    {...(errors.province
                                                        ? {
                                                              appearance:
                                                                  'error',
                                                              message:
                                                                  errors
                                                                      .province
                                                                      .message ||
                                                                  '',
                                                          }
                                                        : {})}
                                                >
                                                    <option
                                                        value=""
                                                        aria-selected
                                                    >
                                                        {t(
                                                            'form.province.default'
                                                        )}
                                                    </option>
                                                    {allProvinces.map(
                                                        (option: string) => (
                                                            <option
                                                                aria-selected
                                                                value={option}
                                                                key={option}
                                                            >
                                                                {titleCaseItalian(
                                                                    option
                                                                )}
                                                            </option>
                                                        )
                                                    )}
                                                </Select>
                                            )}
                                        />
                                    </FieldContainer>
                                    <StyledSelectMessage
                                        id="province-status-message"
                                        role="status"
                                        aria-live="polite"
                                    >
                                        {provinceStatusMessage}
                                    </StyledSelectMessage>
                                    {errors.province ? (
                                        <StyledSelectMessage
                                            id="province-error-message"
                                            role="alert"
                                            aria-live="assertive"
                                        >
                                            {errors.province.message}
                                        </StyledSelectMessage>
                                    ) : undefined}
                                </>
                            ) : undefined}
                            {isDealerSelectionReady ? (
                                <>
                                    <FieldContainer>
                                        <Controller
                                            control={control}
                                            rules={{
                                                required: {
                                                    value: true,
                                                    message: t(
                                                        'form.error.required'
                                                    ),
                                                },
                                            }}
                                            name="dealer"
                                            render={({field: {onChange}}) => (
                                                // @ts-ignore
                                                <Select
                                                    defaultValue=""
                                                    name="dealer"
                                                    onChange={(val) => {
                                                        onChange(val)
                                                        saveDealerSelected(
                                                            val.currentTarget
                                                                .value
                                                        )
                                                    }}
                                                    label={t(
                                                        'form.dealer.label'
                                                    )}
                                                    {...(errors.dealer
                                                        ? {
                                                              appearance:
                                                                  'error',
                                                              message:
                                                                  errors.dealer
                                                                      .message ||
                                                                  '',
                                                          }
                                                        : {})}
                                                >
                                                    <option
                                                        aria-selected
                                                        value=""
                                                    >
                                                        {t(
                                                            'form.dealer.default'
                                                        )}
                                                    </option>

                                                    {!isEmpty(
                                                        filteredDealers
                                                    ) ? (
                                                        <>
                                                            {filteredDealers.map(
                                                                (
                                                                    option: Dealer
                                                                ) => (
                                                                    <option
                                                                        aria-selected
                                                                        key={
                                                                            option.id
                                                                        }
                                                                        value={
                                                                            option.companyCode
                                                                        }
                                                                    >
                                                                        {capitalizeWords(
                                                                            getDealerInfoOption(
                                                                                option
                                                                            )
                                                                        )}
                                                                    </option>
                                                                )
                                                            )}
                                                        </>
                                                    ) : undefined}
                                                </Select>
                                            )}
                                        />
                                    </FieldContainer>
                                    <StyledSelectMessage
                                        id="dealer-status-message"
                                        role="status"
                                        aria-live="polite"
                                    >
                                        {dealerStatusMessage}
                                    </StyledSelectMessage>
                                    {errors.dealer ? (
                                        <StyledSelectMessage
                                            id="dealer-error-message"
                                            role="alert"
                                            aria-live="assertive"
                                        >
                                            {errors.dealer.message}
                                        </StyledSelectMessage>
                                    ) : undefined}
                                </>
                            ) : undefined}
                            {!isDealerSelectionReady ? (
                                <DealerSelectionFeedback
                                    status={dealerSelectionStatus}
                                    onRetry={fetchDealerSelectionData}
                                />
                            ) : undefined}
                        </>
                    ) : (
                        <FieldContainer>
                            <DealerCard>
                                <DealerCardTitle
                                    tag={TextTag.p}
                                    bold
                                    appearance={TokenTextAppearance.bigcopy100}
                                >
                                    {dealerBranches.length && fetchedDealer
                                        ? capitalizeWords(fetchedDealer.company)
                                        : undefined}
                                </DealerCardTitle>
                                {!isEmpty(dealerBranches) ? (
                                    <>
                                        {dealerBranches.length > 1 ? (
                                            <Controller
                                                control={control}
                                                rules={{
                                                    required: {
                                                        value: true,
                                                        message: t(
                                                            'form.error.required'
                                                        ),
                                                    },
                                                }}
                                                name="dealer"
                                                render={({field}) => (
                                                    <Fieldset
                                                        legend={t(
                                                            'form.myDealer.dealerSelectedRadiobutton.legend'
                                                        )}
                                                        appearance={
                                                            errors.dealer
                                                                ? ('error' as InputAppearanceValue)
                                                                : ('default' as InputAppearanceValue)
                                                        }
                                                        message={
                                                            errors.dealer
                                                                ? errors.dealer
                                                                      .message
                                                                : undefined
                                                        }
                                                    >
                                                        {dealerBranches.map(
                                                            (
                                                                option: Dealer
                                                            ) => (
                                                                <RadioButton
                                                                    key={
                                                                        option.id
                                                                    }
                                                                    value={
                                                                        option.companyCode
                                                                    }
                                                                    label={capitalizeWords(
                                                                        getDealerInfoOption(
                                                                            option
                                                                        )
                                                                    )}
                                                                    onChange={
                                                                        field.onChange
                                                                    }
                                                                    name="dealer"
                                                                    onClick={() => {
                                                                        saveDealerSelected(
                                                                            option.companyCode,
                                                                            false
                                                                        )
                                                                    }}
                                                                    checked={
                                                                        field.value ===
                                                                        option.companyCode
                                                                    }
                                                                />
                                                            )
                                                        )}
                                                    </Fieldset>
                                                )}
                                            />
                                        ) : (
                                            <Controller
                                                control={control}
                                                rules={{
                                                    required: {
                                                        value: true,
                                                        message: t(
                                                            'form.error.required'
                                                        ),
                                                    },
                                                }}
                                                name="dealer"
                                                render={({field}) => (
                                                    <>
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={
                                                                dealerBranches[0]
                                                                    .companyCode
                                                            }
                                                        />
                                                        <span>
                                                            {dealerBranches.length &&
                                                            fetchedDealer
                                                                ? capitalizeWords(
                                                                      getDealerAddress(
                                                                          fetchedDealer
                                                                      )
                                                                  )
                                                                : undefined}
                                                        </span>
                                                    </>
                                                )}
                                            />
                                        )}
                                    </>
                                ) : undefined}
                            </DealerCard>
                            <CTA
                                tag="a"
                                emphasis="tertiary"
                                href="#"
                                onClick={() => changeDealer()}
                                aria-label={t(
                                    'form.myDealer.changeDealerCta.label'
                                )}
                            >
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: t(
                                            'form.myDealer.changeDealerCta.label'
                                        ),
                                    }}
                                />
                            </CTA>
                        </FieldContainer>
                    )}

                    {logged ? (
                        <CTAVum
                            tag="a"
                            href="#"
                            emphasis="tertiary"
                            onClick={(e: {preventDefault: () => void}) => {
                                e.preventDefault()
                                openDisclaimerLayer()
                            }}
                            title={t('coupon.disclaimer.ctaDataDisclaimer')}
                            aria-label={t(
                                'coupon.disclaimer.ctaDataDisclaimer'
                            )}
                        >
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: t(
                                        'coupon.disclaimer.ctaDataDisclaimer'
                                    ),
                                }}
                            />
                        </CTAVum>
                    ) : undefined}
                </div>

                <FormPrivacyContainer>
                    <FormPrivacyContainerTitle
                        tag={TextTag.h3}
                        bold
                        appearance={TokenTextAppearance.copy200}
                    >
                        <span
                            dangerouslySetInnerHTML={{
                                __html: t('form.privacy.title'),
                            }}
                        />
                    </FormPrivacyContainerTitle>

                    <CTA
                        tag="a"
                        href={t('form.privacy.privacy_policy_url')}
                        emphasis="tertiary"
                        target="_blank"
                    >
                        <span
                            dangerouslySetInnerHTML={{
                                __html: t('form.privacy.labelCTA'),
                            }}
                        />
                    </CTA>
                    <PrivacyText
                        tag={TextTag.p}
                        appearance={TokenTextAppearance.copy200}
                    >
                        <span
                            dangerouslySetInnerHTML={{
                                __html: t('form.privacy.text'),
                            }}
                        />
                    </PrivacyText>
                    <Fieldset>
                        <Checkbox
                            {...register('privacy_consent', {
                                required: {
                                    value: true,
                                    message: t('form.error.required'),
                                },
                            })}
                            appearance={
                                errors.privacy_consent
                                    ? ('error' as InputAppearanceValue)
                                    : ('default' as InputAppearanceValue)
                            }
                            message={
                                errors.privacy_consent
                                    ? errors.privacy_consent.message
                                    : undefined
                            }
                            label={t('form.privacy.privacy_consent')}
                            value=""
                        />
                        <Checkbox
                            {...register('marketing_consent')}
                            label={t('form.privacy.marketing_consent')}
                            value=""
                        />
                        <Checkbox
                            {...register('profiling_consent')}
                            label={t('form.privacy.profiling_consent')}
                            value=""
                        />
                    </Fieldset>
                </FormPrivacyContainer>

                {showNotification ? (
                    <>
                        <Infobox
                            aria-live="assertive"
                            appearance={'error' as InfoboxAppearanceValue}
                        >
                            <Text tag={TextTag.p}>{NotificationText}</Text>
                        </Infobox>
                    </>
                ) : undefined}

                {loadingForm ? <FormLoadingOverlay /> : undefined}
            </CouponLayerForm>
        </>
    )
}
